import React, { useRef, useState } from "react";
import { Button, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { TableHeader, FiltersButton, InfoBanner, WithRoleAccess } from "@services/ui-components";
import AllMenusTable from "./AllMenusTable";
import AllMenusFilters from "./AllMenusFilters";
import { initialValueFilter, menuTypeKeyMap } from "./constants";
import { useActiveMenusByType } from "./hooks/useActiveMenusByType";
import ActiveMenusByType from "./components/ActiveMenusByType";
import { PartialMenu } from "../../../interfaces/meMenuInterfaces/interfaces";
import ConfirmUnpublishMenuModal from "./modals/ConfirmUnpublishMenuModal/ConfirmUnpublishMenuModal";
import { useMenuStatusActions } from "./hooks/useMenuStatusActions";
import ChangeActiveMenuModal from "./modals/ChangeActiveMenuModal/ChangeActiveMenuModal";
import { cloneNameToDisplay } from "../utils/cloneNameUtils";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

export interface AllMenusFilter {
    search: string;
    menuTypeId: number;
}

const AllMenusPage: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filters, setFilters] = useState<AllMenusFilter>(initialValueFilter);
    const [openCreateFn, setOpenCreateFn] = useState<(() => void) | null>(null);

    const [menuList, setMenuList] = useState<PartialMenu[]>([]);
    const menuListRefetchRef = useRef<() => void>(() => {});
    const [isChangeMenuModalOpen, setIsChangeMenuModalOpen] = useState(false);
    const [menuTypeForChange, setMenuTypeForChange] = useState<typeof menuTypeKeyMap | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const canCloneEditMenu = useRoleAccess(CompanyAccess.MenuCloneEdit);

    const { activeMenusType, replaceableMenus, activeMenusByTypeRefetch } = useActiveMenusByType();
    const { replaceMenu, unpublishMenu, menuAction, setMenuAction, loading } = useMenuStatusActions({
        branchId: Number(branchId),
        t,
        onSuccess: async () => {
            await activeMenusByTypeRefetch();
            menuListRefetchRef.current();
            setMenuAction(null);
        },
        onActiveMenuError: (menu) => {
            setMenuAction({ type: "replace", menu });
        },
    });

    const handleConfirmUnpublish = async () => {
        if (!menuAction || menuAction.type !== "unpublish" || isSubmitting) return;
        try {
            setIsSubmitting(true);
            await unpublishMenu(menuAction.menu);
        } finally {
            setMenuAction(null);
            setIsSubmitting(false);
        }
    };

    const toggleFilters = () => setShowFilters(!showFilters);

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "This page displays a list of all menus. You can create new menus, edit existing ones, or manage their display."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={t("All menu")}
                paths={[
                    { path: `/branches/${branchId}`, label: t("Branches") },
                    { path: "", label: t("All menu") },
                ]}
                colSize={7}
                btnColSize={5}
                buttons={
                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                        <WithRoleAccess permission={CompanyAccess.MenuCreate}>
                            <Col xs={6} xl={7} xxl={5}>
                                <Button
                                    variant="primary"
                                    className="fw-bold w-100 rounded-1"
                                    onClick={() => openCreateFn?.()}
                                >
                                    <i className="bi bi-plus-lg me-2"></i>
                                    <span className="text-truncate text-capitalize">{t("New menu")}</span>
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={6} xl={5} xxl={4}>
                            <FiltersButton filter={filters} showFilters={showFilters} onClick={toggleFilters} />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <AllMenusFilters setFilters={setFilters} />
            </TableHeader>

            <ActiveMenusByType
                menus={activeMenusType}
                canManageActiveMenus={canCloneEditMenu}
                onHideMenu={(shortMenu) => {
                    if (!canCloneEditMenu) return;
                    const fullMenu = menuList.find((m) => m.id === shortMenu.id);
                    setMenuAction({ type: "unpublish", menu: fullMenu || shortMenu });
                }}
                onChangeMenu={(type) => {
                    if (!canCloneEditMenu) return;
                    setMenuTypeForChange(type);
                    setIsChangeMenuModalOpen(true);
                }}
            />
            <AllMenusTable
                filters={filters}
                showAddMenuModal={setOpenCreateFn}
                onMenusLoaded={setMenuList}
                onRefetchReady={(refetchFn) => {
                    menuListRefetchRef.current = refetchFn;
                }}
            />

            {menuAction?.type === "unpublish" && (
                <ConfirmUnpublishMenuModal
                    show={true}
                    onClose={() => setMenuAction(null)}
                    onConfirm={handleConfirmUnpublish}
                    menuName={cloneNameToDisplay(menuAction.menu?.name, t)}
                    loading={loading || isSubmitting}
                />
            )}

            <ChangeActiveMenuModal
                show={isChangeMenuModalOpen}
                handleClose={() => setIsChangeMenuModalOpen(false)}
                replaceableMenus={menuTypeForChange ? replaceableMenus[menuTypeForChange] : []}
                onPublishMenu={async (menu) => {
                  if (!canCloneEditMenu) return;
                  try {
                        setIsSubmitting(true);
                        await replaceMenu(menu);
                        menuListRefetchRef.current();
                        setIsChangeMenuModalOpen(false);
                    } finally {
                        setIsSubmitting(false);
                    }
                }}
                loading={loading || isSubmitting}
            />
        </div>
    );
};

export default AllMenusPage;
